import dayjs from "dayjs";
import { FileText, Trash2, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import AppAlertDialog from "@/components/Core/AppAlertDialog/AppAlertDialog";
import { FormCard } from "@/components/Core/FormCard";
import FileImporter from "@/components/Shared/FileImporter/FileImporter";
import CertificationChip from "@/components/Shared/TagChip/CertificationChip";
import Consultant, {
	ConsultantCertification,
	ConsultantCertificationProof,
} from "@/models/Consultant";
import { CertificationType } from "@/models/Organisation";
import { useOrganisation } from "@/networking/organisation.hooks";
import { useUpdateCandidate } from "@/networking/candidate.hooks";
import { useUpdateConsultant } from "@/networking/consultant.hooks";
import {
	certificationTypeName,
	defaultExpiryFromValidity,
	isCredentialValid,
} from "@/utils/credential.utils";
import { useMediaStore } from "../../stores/media.store";

const DATE_INPUT_CLS = "rounded-md border border-solid border-gray-300 px-2 py-1.5 text-sm";
const ADD_BUTTON_CLS =
	"rounded-md border-0 bg-black px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50";

/**
 * How many proof files an admin may attach to one certification. A client-side
 * rule only — the backend schema allows up to 10 so the cap can be relaxed here
 * without a service deploy.
 */
const MAX_PROOF_FILES = 3;

/** Display name of an uploaded proof file; pre-migration entries stored none. */
const proofFileName = (file: ConsultantCertificationProof) => file.name || "Underlag";

/**
 * The proof files of an assigned certification, newest shape first. The single
 * `proof` fallback covers entries not yet touched by the
 * multiple-certification-proofs migration and goes away once it has run.
 */
const proofFilesOf = ({
	proof,
	proofs,
}: ConsultantCertification): ConsultantCertificationProof[] => {
	if (proofs?.length) return proofs;
	return proof ? [proof] : [];
};

/**
 * "Utgången" badge for expired certifications — they stay listed and marked.
 */
const ExpiredBadge: React.FC = () => (
	<span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
		Utgången
	</span>
);

const formatExpiry = (expiresAt?: string | null) =>
	expiresAt ? `Giltig t.o.m. ${dayjs(expiresAt).format("YYYY-MM-DD")}` : "Ingen utgångstid";

/** The chosen YYYY-MM-DD expiry date, stored as the end of that day (ISO). */
const toStoredExpiry = (date: string): string | undefined =>
	date ? dayjs(date).endOf("day").toISOString() : undefined;

/**
 * The certifikat panel on the consultant "Kompetens" tab: assign/remove
 * org-defined certifications (with an editable expiry date and an uploaded
 * proof document; consultant-app visibility is an org-level setting on the
 * certification type). Persists through the ordinary consultant-account
 * update, riding the profile like all other fields. (Consultant tags are
 * managed from the detail-page header, not here.)
 *
 * Also used on the Recruitment "Kompetens" tab — candidates are consultant
 * accounts, but persist through the candidate endpoint (`isCandidate`) so the
 * candidate query cache is invalidated.
 */
const ConsultantCredentialsPanel: React.FC<{ consultant: Consultant; isCandidate?: boolean }> = ({
	consultant,
	isCandidate = false,
}) => {
	const mediaStore = useMediaStore();
	const { data: organisation } = useOrganisation();
	const { mutateAsync: updateConsultant, isLoading: isSavingConsultant } = useUpdateConsultant();
	const { mutateAsync: updateCandidate, isLoading: isSavingCandidate } = useUpdateCandidate();
	const isSaving = isCandidate ? isSavingCandidate : isSavingConsultant;

	const certifications = consultant.profile?.certifications ?? [];

	const certificationTypes = organisation?.certificationTypes ?? [];

	// --- Add-certification form state ---
	const [certTypeKey, setCertTypeKey] = useState("");
	const [certExpiry, setCertExpiry] = useState("");
	const [certToRemove, setCertToRemove] = useState<ConsultantCertification | null>(null);
	/** Files uploaded for the certification about to be assigned, accumulated across drops. */
	const [proofFiles, setProofFiles] = useState<ConsultantCertificationProof[]>([]);
	/**
	 * Remounts FileImporter after each harvest. The importer keeps the last drop in
	 * its own state and renders those filenames inside the drop zone; resetting it
	 * leaves the chips below as the single list of staged files.
	 */
	const [importerKey, setImporterKey] = useState(0);

	const selectedCertType: CertificationType | undefined = certificationTypes.find(
		(c) => c.key === certTypeKey
	);
	const {
		document: uploadedDocument,
		documentList: uploadedDocumentList,
		documentLoading,
	} = mediaStore;

	// FileImporter writes a single dropped file to mediaStore.document and several to
	// mediaStore.documentList, replacing whichever slot on every drop — and the store
	// is a global singleton. Move each upload into local state and clear the store so
	// files add up across drops instead of overwriting one another.
	useEffect(() => {
		const dropped = [
			...(uploadedDocumentList ?? []),
			...(uploadedDocument ? [uploadedDocument] : []),
		];
		if (!dropped.length) return;

		setProofFiles((prev) =>
			[
				...prev,
				...dropped
					.filter(({ url }) => !prev.some((file) => file.url === url))
					.map(({ url, mimeType, originalName }) => ({ url, mimeType, name: originalName })),
			].slice(0, MAX_PROOF_FILES)
		);

		mediaStore.removeDocument();
		mediaStore.removeDocumentList();
		setImporterKey((key) => key + 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadedDocument, uploadedDocumentList]);

	const availableCertTypes = certificationTypes.filter(
		(c) => !certifications.some((assigned) => assigned.typeKey === c.key)
	);

	const saveCertifications = (next: ConsultantCertification[]) =>
		(isCandidate ? updateCandidate : updateConsultant)({
			userId: consultant.id,
			update: { profile: { certifications: next } },
		});

	const clearStagedProofs = () => {
		setProofFiles([]);
		mediaStore.removeDocument();
		mediaStore.removeDocumentList();
		setImporterKey((key) => key + 1);
	};

	const handleCertTypeChange = (key: string) => {
		setCertTypeKey(key);
		// Staged files belong to the certification they were uploaded for.
		clearStagedProofs();
		const type = certificationTypes.find((c) => c.key === key);
		// The date is only relevant for types with an expiration date; prefill
		// the default from a legacy org-level validityDuration when present.
		setCertExpiry(
			type?.hasExpirationDate ? defaultExpiryFromValidity(type?.validityDuration) ?? "" : ""
		);
	};

	const handleAddCertification = async () => {
		if (!selectedCertType) return;
		if (selectedCertType.hasExpirationDate && !certExpiry) return;
		const entry: ConsultantCertification = {
			typeKey: selectedCertType.key,
			name: selectedCertType.name.sv,
			assignedAt: new Date().toISOString(),
			// Types without hasExpirationDate never expire — no date is entered.
			expiresAt: selectedCertType.hasExpirationDate ? toStoredExpiry(certExpiry) ?? null : null,
			...(proofFiles.length ? { proofs: proofFiles } : {}),
		};
		await saveCertifications([...certifications, entry]);
		clearStagedProofs();
		setCertTypeKey("");
		setCertExpiry("");
	};

	const handleRemoveCertification = async () => {
		if (!certToRemove) return;
		await saveCertifications(certifications.filter((c) => c.typeKey !== certToRemove.typeKey));
		setCertToRemove(null);
	};

	const missingRequiredProof = !!selectedCertType?.requiresProof && proofFiles.length === 0;
	const missingRequiredDate = !!selectedCertType?.hasExpirationDate && !certExpiry;

	return (
		<div>
			<div className="flex flex-col gap-5">
				<FormCard title="Certifikat" skipTranslation>
					{certifications.length === 0 ? (
						<p className="text-sm text-gray-500">Inga certifikat tilldelade.</p>
					) : (
						<div>
							{certifications.map((cert) => (
								<div
									key={cert.typeKey}
									className="flex items-start justify-between gap-3 border-t border-gray-200 py-3 first:border-t-0 first:pt-0"
								>
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-2">
											<CertificationChip name={certificationTypeName(organisation, cert.typeKey)} />
											{!isCredentialValid(cert.expiresAt) && <ExpiredBadge />}
										</div>
										<div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
											<span>Tilldelad {dayjs(cert.assignedAt).format("YYYY-MM-DD")}</span>
											<span>{formatExpiry(cert.expiresAt)}</span>
										</div>
										{proofFilesOf(cert).length > 0 && (
											<div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
												{proofFilesOf(cert).map((file, i) => (
													<a
														key={`${file.url}-${i}`}
														href={file.url}
														target="_blank"
														rel="noopener noreferrer"
														className="inline-flex max-w-full items-center gap-1 text-blue-700 underline"
													>
														<FileText size={12} className="shrink-0" />
														<span className="truncate">{proofFileName(file)}</span>
													</a>
												))}
											</div>
										)}
									</div>
									<button
										type="button"
										onClick={() => setCertToRemove(cert)}
										disabled={isSaving}
										aria-label="Ta bort certifikat"
										title="Ta bort"
										className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-transparent bg-transparent text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
									>
										<Trash2 size={16} />
									</button>
								</div>
							))}
						</div>
					)}

					<div className="mt-4 border-t border-gray-200 pt-4">
						<h3 className="mb-2 text-sm font-semibold text-gray-700">Tilldela certifikat</h3>
						{availableCertTypes.length === 0 ? (
							<p className="text-sm text-gray-500">
								{certificationTypes.length === 0
									? "Inga certifikattyper finns definierade, ändra det i organisationsinställningar."
									: "Alla certifikattyper är redan tilldelade."}
							</p>
						) : (
							<div className="flex flex-col gap-3">
								<div className="flex flex-wrap items-center gap-2">
									<select
										value={certTypeKey}
										onChange={(e) => handleCertTypeChange(e.target.value)}
										className="rounded-md border border-solid border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
									>
										<option value="">Välj certifikat…</option>
										{availableCertTypes.map((c) => (
											<option key={c.key} value={c.key}>
												{c.name.sv}
											</option>
										))}
									</select>
									{selectedCertType?.hasExpirationDate && (
										<label className="flex items-center gap-1.5 text-sm text-gray-700">
											Utgår *
											<input
												type="date"
												value={certExpiry}
												onChange={(e) => setCertExpiry(e.target.value)}
												className={DATE_INPUT_CLS}
											/>
										</label>
									)}
								</div>
								{selectedCertType && (
									<div>
										<p className="mb-1 text-xs font-semibold text-gray-600">
											Underlag (dokument)
											{selectedCertType.requiresProof ? " *" : ""}
											<span className="ml-1 font-normal text-gray-500">
												({proofFiles.length}/{MAX_PROOF_FILES})
											</span>
										</p>
										{proofFiles.length > 0 && (
											<div className="mb-2 flex flex-wrap gap-2">
												{proofFiles.map((file) => (
													<span
														key={file.url}
														className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 py-1 pl-2.5 pr-1.5 text-xs text-gray-700"
													>
														<FileText size={12} className="shrink-0 text-gray-400" />
														<span className="truncate">{proofFileName(file)}</span>
														<button
															type="button"
															onClick={() =>
																setProofFiles((prev) => prev.filter((f) => f.url !== file.url))
															}
															aria-label={`Ta bort ${proofFileName(file)}`}
															title="Ta bort"
															className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent p-0 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
														>
															<X size={10} />
														</button>
													</span>
												))}
											</div>
										)}
										{proofFiles.length < MAX_PROOF_FILES ? (
											// Remounted after every drop; the remaining slot count is what the
											// drop zone rejects against, so files add up to MAX_PROOF_FILES.
											<FileImporter
												key={importerKey}
												maxFiles={MAX_PROOF_FILES - proofFiles.length}
												multipleFiles
											/>
										) : (
											<p className="text-xs text-gray-500">
												Max {MAX_PROOF_FILES} filer. Ta bort en fil för att ladda upp en annan.
											</p>
										)}
									</div>
								)}
								<div>
									<button
										type="button"
										onClick={handleAddCertification}
										disabled={
											!selectedCertType ||
											missingRequiredProof ||
											missingRequiredDate ||
											documentLoading ||
											isSaving
										}
										className={ADD_BUTTON_CLS}
									>
										Tilldela
									</button>
									{missingRequiredProof && (
										<p className="mt-1 text-xs text-amber-700">
											Ladda upp minst en fil som underlag för att kunna tilldela certifikatet.
										</p>
									)}
									{missingRequiredDate && (
										<p className="mt-1 text-xs text-amber-700">
											Ange ett utgångsdatum för att kunna tilldela certifikatet.
										</p>
									)}
								</div>
							</div>
						)}
					</div>
				</FormCard>
			</div>

			<AppAlertDialog
				open={!!certToRemove}
				onOpenChange={(open) => {
					if (!open) setCertToRemove(null);
				}}
				variant="danger"
				title={`Ta bort ${
					certToRemove ? certificationTypeName(organisation, certToRemove.typeKey) : ""
				}?`}
				description="Certifikatet och dess underlag tas bort från konsulten. Detta går inte att ångra."
				confirmLabel="Ta bort"
				cancelLabel="Avbryt"
				onConfirm={handleRemoveCertification}
			/>
		</div>
	);
};
export default ConsultantCredentialsPanel;
